import React, { useMemo, useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  IconClipboard, IconUsers, IconRun, IconAward, IconMapPin,
  IconCalendar, IconPackage, IconCurrencyDollar, IconCash,
  IconCoin, IconReceipt, IconAlertCircle, IconAlertTriangle,
  IconBriefcase, IconTool, IconMessage, IconUserPlus,
  IconSchool, IconStar, IconCalendarStats, IconCar,
  IconShield, IconHome, IconTrophy, IconBook, IconMail,
  IconCrown, IconWorld, IconBus, IconShirt, IconTree,
  IconReportMoney, IconX, IconSearch, IconClock, IconRocket,
  IconRobot, IconList, IconTerminal2,
  IconEye, IconWallet, IconShieldCheck, IconFileCheck,
  IconSpeakerphone, IconSettings, IconClipboardCheck, IconPhone
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import {
  BRICK_NAVIGATION,
  getAllBricks,
  filterBricksByBadges,
  getBrickByRoute
} from '@/constants/brick-navigation';
import { GARAGES } from '@/constants/garages';
import { useBrickUsage } from '@/hooks/useBrickUsage';
import { usePendingWorkCounts } from '@/hooks/usePendingWorkCounts';
import { pendingLabel } from '@/constants/pendingWork';
import { useAuth } from '@/contexts/AuthContext';

// Metallic gold animations
const sidebarAnimations = `
  @keyframes shimmer-flow {
    0% { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }
  @keyframes metallic-shine {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
`;

// Inject animations (once)
if (typeof document !== 'undefined' && !document.getElementById('sidebar-animations')) {
  const style = document.createElement('style');
  style.id = 'sidebar-animations';
  style.innerHTML = sidebarAnimations;
  document.head.appendChild(style);
}

// Icon component map
const ICON_COMPONENTS = {
  IconClipboard, IconUsers, IconRun, IconAward, IconMapPin,
  IconCalendar, IconPackage, IconCurrencyDollar, IconCash,
  IconCoin, IconReceipt, IconAlertCircle, IconAlertTriangle,
  IconBriefcase, IconTool, IconMessage, IconUserPlus,
  IconSchool, IconStar, IconCalendarStats, IconCar,
  IconShield, IconHome, IconTrophy, IconBook, IconMail,
  IconCrown, IconWorld, IconBus, IconShirt, IconTree,
  IconReportMoney,
  IconRocket,
  IconRobot,
  IconList,
  IconTerminal2,
  IconEye,
  IconWallet,
  IconShieldCheck,
  IconFileCheck,
  IconSpeakerphone,
  IconSettings,
  IconClipboardCheck,
  IconPhone
};

/**
 * BricksSidebar - Lot loader with search + recent history
 *
 * Features:
 * - Search bar at top to filter lots
 * - Last 10 viewed lots (recency-based)
 * - All lots below, alphabetically sorted
 */
export default function BricksSidebar({ collapsed, onMobileClose }) {
  const location = useLocation();
  const { userBadges = [], userRole } = useAuth();
  const { trackUsage, getRecentBricks, usageData } = useBrickUsage();
  // Pending work waiting on a human, per lot (see constants/pendingWork.js).
  const { counts: pendingCounts } = usePendingWorkCounts();
  const [searchQuery, setSearchQuery] = useState('');

  // Admin/council/owner sees all lots regardless of badges
  const isAdmin = userRole === 'owner' || userRole === 'admin' || userBadges.includes('council');

  // Get all accessible lots
  const allLots = useMemo(() => {
    const lots = getAllBricks(false); // exclude coming-soon
    // Admins see all lots, others get filtered by badges
    if (isAdmin) return lots;
    return filterBricksByBadges(lots, userBadges);
  }, [userBadges, isAdmin]);

  // Get last 10 viewed lots
  const recentLots = useMemo(() => {
    const recent = getRecentBricks(10);
    // Map IDs to full lot objects
    return recent
      .map(r => allLots.find(b => b.id === r.id))
      .filter(Boolean);
  }, [getRecentBricks, allLots]);

  // Filter lots by search query
  const filteredLots = useMemo(() => {
    if (!searchQuery.trim()) return allLots;
    const query = searchQuery.toLowerCase();
    return allLots.filter(lot =>
      lot.name.toLowerCase().includes(query) ||
      lot.id.toLowerCase().includes(query)
    );
  }, [allLots, searchQuery]);

  // Sort alphabetically for "All Lots" section
  const sortedLots = useMemo(() => {
    return [...filteredLots].sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredLots]);

  // Garages whose name matches the current search query
  const matchingGarages = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return GARAGES
      .filter(g => g.name.toLowerCase().includes(q))
      .map(g => ({
        id: g.id,
        name: g.name,
        icon: g.icon,
        lots: g.lots
          .map(lot => allLots.find(b => b.route === lot.path) || {
            id: `${g.id}.${lot.id}`,
            name: lot.name,
            icon: lot.icon,
            route: lot.path,
            requiredBadges: lot.requiredBadges || []
          })
          .filter(l => isAdmin || !l.requiredBadges?.length || (l.requiredBadges || []).some(b => userBadges.includes(b)))
      }))
      .filter(g => g.lots.length > 0);
  }, [searchQuery, allLots, isAdmin, userBadges]);

  // IDs already rendered under pinned garages — exclude from the lot-match list
  const pinnedLotIds = useMemo(() => {
    const ids = new Set();
    matchingGarages.forEach(g => g.lots.forEach(l => ids.add(l.id)));
    return ids;
  }, [matchingGarages]);

  // Lot-match results with pinned lots removed to prevent duplicates
  const dedupedSortedLots = useMemo(() => {
    if (!matchingGarages.length) return sortedLots;
    return sortedLots.filter(l => !pinnedLotIds.has(l.id));
  }, [sortedLots, matchingGarages, pinnedLotIds]);

  // Current route detection
  const currentLot = useMemo(() => getBrickByRoute(location.pathname), [location.pathname]);

  // Track navigation
  const handleNavClick = useCallback((brickId) => {
    trackUsage(brickId);
    setSearchQuery(''); // Clear search on selection
    if (onMobileClose) onMobileClose();
  }, [trackUsage, onMobileClose]);

  const renderNavItem = (lot, isActive, showTime = false) => {
    const IconComponent = ICON_COMPONENTS[lot.icon] || IconClipboard;
    const pendingCount = pendingCounts[lot.id] || 0;
    const lastAccess = usageData[lot.id]?.lastAccess;
    const timeAgo = lastAccess ? getTimeAgo(lastAccess) : null;

    return (
      <Link
        key={lot.id}
        to={lot.route}
        onClick={() => handleNavClick(lot.id)}
        title={lot.name}
        className={cn(
          'flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-300 group relative overflow-hidden',
          isActive
            ? 'bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 shadow-[0_4px_12px_rgba(0,0,0,0.4),0_0_12px_rgba(174,163,109,0.3)] border border-vegas-gold/60'
            : 'bg-gray-800/40 hover:bg-gray-700/60 border border-transparent hover:border-vegas-gold/30 hover:shadow-[0_0_8px_rgba(174,163,109,0.15)]'
        )}
      >
        {/* Shimmer overlay for active */}
        {isActive && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(174,163,109,0.08) 25%, rgba(212,175,55,0.12) 50%, rgba(174,163,109,0.08) 75%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer-flow 3s linear infinite'
            }}
          />
        )}
        <IconComponent
          size={19}
          stroke={1.5}
          className="shrink-0 relative z-10"
          style={{
            color: isActive ? '#D4AF37' : '#8B7355',
            filter: isActive
              ? 'drop-shadow(0 0 6px rgba(212,175,55,0.6))'
              : 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
            transition: 'all 0.3s'
          }}
        />
        {!collapsed && (
          <>
            <span
              className="flex-1 text-[13px] uppercase tracking-normal font-body font-medium relative z-10 truncate"
              style={isActive ? {
                background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 40%, #D4AF37 60%, #B8860B 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'metallic-shine 3s linear infinite'
              } : {
                color: '#9B8357'
              }}
            >
              {lot.name}
            </span>
            {pendingCount > 0 && (
              <span
                title={pendingLabel(lot.id, pendingCount)}
                className="flex-shrink-0 relative z-10 min-w-[18px] px-1.5 py-0.5 rounded-full text-[11px] font-semibold text-center bg-amber-500/20 text-amber-300"
              >
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
            {showTime && timeAgo && (
              <span className="text-[12px] text-gray-600 flex-shrink-0 relative z-10">
                {timeAgo}
              </span>
            )}
          </>
        )}
      </Link>
    );
  };

  return (
    <div className="h-full text-gray-300 flex flex-col relative overflow-hidden">
      {/* Multi-layer gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-800 via-gray-900 to-black" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(174,163,109,0.1) 10px, rgba(174,163,109,0.1) 11px)'
      }} />
      {/* Vegas gold accent border */}
      <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-vegas-gold/40 via-vegas-gold/20 to-transparent" />

      {/* Content wrapper */}
      <div className="relative z-10 h-full flex flex-col">
      {/* Mobile Close Button */}
      {onMobileClose && (
        <div className="flex justify-end p-2 border-b border-gray-800 lg:hidden">
          <button
            onClick={onMobileClose}
            className="p-1 hover:bg-gray-800 rounded"
          >
            <IconX size={20} />
          </button>
        </div>
      )}

      {/* Search Bar */}
      {!collapsed && (
        <div className="px-1.5 py-1.5 border-b border-gray-800/60">
          <div className="relative">
            <IconSearch
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2"
              style={{ color: '#8B7355' }}
            />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-900/80 border border-gray-700/60 rounded-lg pl-7 pr-7 py-1.5 text-[12px] text-white placeholder-gray-500 focus:outline-none focus:border-vegas-gold/50 focus:shadow-[0_0_8px_rgba(174,163,109,0.2)] transition-all duration-300"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-700 rounded transition-colors"
              >
                <IconX size={12} className="text-gray-500 hover:text-gray-300" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto p-1.5">
        {/* Search Results */}
        {searchQuery ? (
          <div className="space-y-1">
            {/* Pinned garage sections */}
            {matchingGarages.map(garage => {
              const GarageIcon = ICON_COMPONENTS[garage.icon] || IconClipboard;
              return (
                <div key={garage.id} className="mb-2">
                  <div className="flex items-center gap-1.5 px-1.5 py-1 mb-1 relative">
                    <GarageIcon size={12} style={{ color: '#D4AF37', filter: 'drop-shadow(0 0 3px rgba(212,175,55,0.4))' }} />
                    <span
                      className="text-[11px] uppercase tracking-wide font-semibold"
                      style={{
                        background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 50%, #D4AF37 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      Garage: {garage.name}
                    </span>
                    <div className="flex-1 h-[1px] ml-1 bg-gradient-to-r from-vegas-gold/40 to-transparent" />
                  </div>
                  <div className="space-y-0.5 pl-4">
                    {garage.lots.map(lot =>
                      renderNavItem(lot, currentLot?.id === lot.id)
                    )}
                  </div>
                </div>
              );
            })}
            {/* Deduped lot-name matches */}
            {dedupedSortedLots.length > 0 && (
              <div className="space-y-0.5">
                {dedupedSortedLots.map(lot =>
                  renderNavItem(lot, currentLot?.id === lot.id)
                )}
              </div>
            )}
            {/* Empty state */}
            {matchingGarages.length === 0 && dedupedSortedLots.length === 0 && (
              <div className="px-3 py-8 text-center text-gray-500 text-sm">
                No lots found for &quot;{searchQuery}&quot;
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Recent Section - Last 10 Viewed */}
            {recentLots.length > 0 && !collapsed && (
              <div className="mb-2">
                <div className="flex items-center gap-1.5 px-1.5 py-1 mb-1 relative">
                  <IconClock size={12} style={{ color: '#D4AF37', filter: 'drop-shadow(0 0 3px rgba(212,175,55,0.4))' }} />
                  <span
                    className="text-[11px] uppercase tracking-wide font-semibold"
                    style={{
                      background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 50%, #D4AF37 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    Recent
                  </span>
                  <div className="flex-1 h-[1px] ml-1 bg-gradient-to-r from-vegas-gold/40 to-transparent" />
                </div>
                <div className="space-y-0.5">
                  {recentLots.map(lot =>
                    renderNavItem(lot, currentLot?.id === lot.id, true)
                  )}
                </div>
              </div>
            )}

            {/* All Lots Section */}
            {!collapsed && (
              <div>
                {recentLots.length > 0 && (
                  <div className="flex items-center gap-1.5 px-1.5 py-1 mt-2 mb-1">
                    <span
                      className="text-[11px] uppercase tracking-wide font-semibold"
                      style={{
                        background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 50%, #D4AF37 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      All Lots
                    </span>
                    <div className="flex-1 h-[1px] ml-1 bg-gradient-to-r from-vegas-gold/40 to-transparent" />
                  </div>
                )}
                <div className="space-y-0.5">
                  {sortedLots.map(lot =>
                    renderNavItem(lot, currentLot?.id === lot.id)
                  )}
                </div>
              </div>
            )}

            {/* Collapsed Mode - Just icons */}
            {collapsed && (
              <div className="space-y-0.5">
                {sortedLots.slice(0, 15).map(lot =>
                  renderNavItem(lot, currentLot?.id === lot.id)
                )}
              </div>
            )}
          </>
        )}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="p-1.5 border-t border-gray-800/60 bg-gradient-to-t from-black/40 to-transparent">
          <div className="text-[11px] text-gray-600 text-center">
            {allLots.length} lots
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

/**
 * Format timestamp to relative time
 */
function getTimeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return `${Math.floor(seconds / 604800)}w ago`;
}
